/**
 * API Service Module for LandGrid.AI
 * Handles all backend API communication
 */

const API_MODE = (process.env.VITE_API_MODE || 'express').toLowerCase();
const API_TIMEOUT = parseInt(process.env.VITE_API_TIMEOUT, 10) || 10000;

function resolveDefaultBaseURL() {
  if (API_MODE === 'netlify') {
    if (process.env.VITE_API_BASE_URL) {
      return process.env.VITE_API_BASE_URL;
    }
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8888/.netlify/functions';
    }
    return '/.netlify/functions';
  }

  // Default Express stub used during MD01
  return process.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
}

const API_BASE_URL = resolveDefaultBaseURL().replace(/\/$/, '');

export class APIService {
  constructor() {
    this.mode = API_MODE;
    this.baseURL = API_BASE_URL;
    this.timeout = API_TIMEOUT;
    this.initialized = false;
    this.supportsAuth = this.mode === 'netlify' || process.env.VITE_API_ENABLE_AUTH === 'true';

    console.log(`API Service initialized in ${this.mode} mode (base: ${this.baseURL})`);
  }

  /**
   * Initialize the API service
   */
  async initialize() {
    try {
      const healthEndpoint = this.mode === 'netlify' ? '/health' : '/status';
      await this.request(healthEndpoint);
      console.log('API Service initialisation health check passed');
      this.initialized = true;
      return true;
    } catch (error) {
      console.warn('API service unavailable, running in offline mode:', error.message);
      this.initialized = false;
      // Return true to continue with local data instead of failing
      return true;
    }
  }

  /**
   * Generic request method with timeout and improved error handling
   */
  async request(endpoint, options = {}) {
    const normalisedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${this.baseURL}${normalisedEndpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: controller.signal
    };

    const finalOptions = { ...defaultOptions, ...options };

    if (finalOptions.body && typeof finalOptions.body !== 'string') {
      finalOptions.body = JSON.stringify(finalOptions.body);
    }

    try {
      const response = await fetch(url, finalOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await this.parseResponseBody(response);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        const timeoutError = new Error(`Request timeout after ${this.timeout}ms`);
        timeoutError.name = 'TimeoutError';
        throw timeoutError;
      }

      // Only log error in development mode
      if (process.env.NODE_ENV === 'development' || process.env.VITE_DEBUG_MODE === 'true') {
        console.error(`API request failed: ${finalOptions.method} ${url}`, error);
      }
      throw error;
    }
  }

  extractData(response) {
    if (response === null) return null;
    if (Array.isArray(response)) return response;
    if (typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')) {
      return response.data;
    }
    return response;
  }

  /**
   * Safely parse a fetch response, handling empty bodies
   */
  async parseResponseBody(response) {
    if (response.status === 204 || response.status === 205) {
      return null;
    }

    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return null;
    }

    const contentType = response.headers.get('content-type') || '';
    const rawBody = await response.text();

    if (!rawBody) {
      return null;
    }

    if (contentType.includes('application/json')) {
      try {
        return JSON.parse(rawBody);
      } catch (error) {
        if (process.env.NODE_ENV === 'development' || process.env.VITE_DEBUG_MODE === 'true') {
          console.warn('API response JSON parse failed, returning raw text', error);
        }
      }
    }

    return rawBody;
  }

  storeSession(accessToken, refreshToken, user) {
    if (accessToken) {
      localStorage.setItem('landgrid_access_token', accessToken);
    }
    if (refreshToken) {
      localStorage.setItem('landgrid_refresh_token', refreshToken);
    }
    if (user) {
      localStorage.setItem('landgrid_user', JSON.stringify(user));
    }
  }

  clearSession() {
    localStorage.removeItem('landgrid_access_token');
    localStorage.removeItem('landgrid_refresh_token');
    localStorage.removeItem('landgrid_user');
  }

  createOfflineSession(email, overrides = {}) {
    const now = Date.now();
    const user = {
      id: overrides.id || 'offline-user',
      email,
      role: overrides.role || 'offline',
      firstName: overrides.firstName || 'Offline',
      lastName: overrides.lastName || 'User',
      createdAt: overrides.createdAt || new Date(now).toISOString(),
      offline: true,
      ...overrides
    };

    const session = {
      accessToken: `offline-access-${now}`,
      refreshToken: `offline-refresh-${now}`,
      user,
      offline: true
    };

    this.storeSession(session.accessToken, session.refreshToken, session.user);
    this.initialized = false; // force offline mode for subsequent calls

    return {
      success: true,
      data: session
    };
  }

  /**
   * Get all available biomes
   */
  async getBiomes() {
    if (!this.initialized) {
      console.log('API not available, using local biome data');
      return null; // Signal to use local data
    }

    try {
      const endpoint = this.mode === 'netlify'
        ? '/content-api?type=biomes'
        : '/biomes';
      const response = await this.request(endpoint);
      return this.extractData(response);
    } catch (error) {
      console.warn('Failed to fetch biomes from API, falling back to local data:', error.message);
      return null; // Signal to use local data
    }
  }

  /**
   * Get grid data
   */
  async getGridData(lat, lng, zoom) {
    if (!this.initialized) {
      console.log('API not available, using local grid data');
      return null; // Signal to use local data
    }

    try {
      const params = new URLSearchParams();
      if (lat !== undefined) params.append('lat', lat);
      if (lng !== undefined) params.append('lng', lng);
      if (zoom !== undefined) params.append('zoom', zoom);

      let endpoint;
      if (this.mode === 'netlify') {
        const netlifyParams = new URLSearchParams({ type: 'grid' });
        params.forEach((value, key) => netlifyParams.append(key, value));
        endpoint = `/content-api?${netlifyParams.toString()}`;
      } else {
        const queryString = params.toString();
        endpoint = queryString ? `/grid?${queryString}` : '/grid';
      }

      const response = await this.request(endpoint);
      return this.extractData(response);
    } catch (error) {
      console.warn('Failed to fetch grid data from API, falling back to local data:', error.message);
      return null; // Signal to use local data
    }
  }

  /**
   * Get quiz questions
   */
  async getQuizQuestions(quizId) {
    if (!this.initialized) {
      console.log('API not available, using local quiz questions');
      return null; // Signal to use local data
    }

    try {
      const params = new URLSearchParams();
      if (quizId) params.append('id', quizId);

      let endpoint;
      if (this.mode === 'netlify') {
        const netlifyParams = new URLSearchParams({ type: 'quiz' });
        params.forEach((value, key) => netlifyParams.append(key, value));
        endpoint = `/content-api?${netlifyParams.toString()}`;
      } else {
        const queryString = params.toString();
        endpoint = queryString ? `/quiz?${queryString}` : '/quiz';
      }

      const response = await this.request(endpoint);
      return this.extractData(response);
    } catch (error) {
      console.warn('Failed to fetch quiz questions from API, falling back to local data:', error.message);
      return null; // Signal to use local data
    }
  }

  /**
   * Submit quiz answers
   */
  async submitQuizAnswers(answers, userId = 'anonymous') {
    if (!this.initialized) {
      console.log('API not available, storing quiz results locally');
      // Store results in localStorage for offline mode
      const results = {
        answers,
        userId,
        timestamp: Date.now(),
        score: this.calculateLocalScore(answers)
      };
      localStorage.setItem('landgrid_quiz_results', JSON.stringify(results));
      return results;
    }

    try {
      const response = await this.request('/quiz/submit', {
        method: 'POST',
        body: {
          answers,
          userId
        }
      });
      return this.extractData(response);
    } catch (error) {
      console.warn('Failed to submit quiz answers to API, storing locally:', error.message);
      // Fallback to local storage
      const results = {
        answers,
        userId,
        timestamp: Date.now(),
        score: this.calculateLocalScore(answers)
      };
      localStorage.setItem('landgrid_quiz_results', JSON.stringify(results));
      return results;
    }
  }

  /**
   * Authentication methods
   */
  async login(email, password, rememberMe = false) {
    if (!this.supportsAuth) {
      console.info('Auth API disabled in current mode. Creating offline session.');
      return this.createOfflineSession(email, { rememberMe });
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/auth-login' : '/auth/login';
      const response = await this.request(endpoint, {
        method: 'POST',
        body: { email, password, rememberMe }
      });

      const data = this.extractData(response);
      if (data?.accessToken) {
        this.storeSession(data.accessToken, data.refreshToken, data.user);
      }

      if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'success')) {
        return { ...response, data };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async register(userData) {
    if (!this.supportsAuth) {
      console.info('Registration API disabled in current mode. Creating offline session.');
      return this.createOfflineSession(userData?.email || 'offline@landgrid.ai', userData);
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/auth-register' : '/auth/register';
      const response = await this.request(endpoint, {
        method: 'POST',
        body: userData
      });

      const data = this.extractData(response);
      if (data?.accessToken) {
        this.storeSession(data.accessToken, data.refreshToken, data.user);
      }

      if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'success')) {
        return { ...response, data };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }

  async refreshToken() {
    if (!this.supportsAuth) {
      const user = this.getStoredUser() || { email: 'offline@landgrid.ai' };
      console.info('Refresh token requested in offline mode. Renewing offline session.');
      return this.createOfflineSession(user.email, user);
    }

    try {
      const refreshToken = localStorage.getItem('landgrid_refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const endpoint = this.mode === 'netlify' ? '/auth-refresh' : '/auth/refresh';
      const response = await this.request(endpoint, {
        method: 'POST',
        body: { refreshToken }
      });

      const data = this.extractData(response);
      if (data?.accessToken) {
        this.storeSession(data.accessToken, data.refreshToken, data.user);
      }

      if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'success')) {
        return { ...response, data };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Token refresh failed:', error);
      await this.logout(); // Clear invalid tokens
      throw error;
    }
  }

  async getCurrentUser() {
    if (!this.supportsAuth) {
      const user = this.getStoredUser();
      if (user) {
        return { success: true, data: { user, offline: true } };
      }
      throw new Error('Offline session not found. Please login (offline mode).');
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/auth-me' : '/auth/me';
      const response = await this.requestWithAuth(endpoint);
      return response;
    } catch (error) {
      console.error('Get current user failed:', error);
      throw error;
    }
  }

  async logout(allSessions = false) {
    if (!this.supportsAuth) {
      this.clearSession();
      return { success: true, offline: true };
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/auth-logout' : '/auth/logout';
      const payload = {
        method: 'POST',
        body: { allSessions }
      };

      if (this.mode === 'netlify') {
        await this.request(endpoint, payload);
      } else if (localStorage.getItem('landgrid_access_token')) {
        await this.requestWithAuth(endpoint, payload);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development' || process.env.VITE_DEBUG_MODE === 'true') {
        console.warn('Logout request failed:', error);
      }
    } finally {
      this.clearSession();
    }

    return { success: true };
  }

  isAuthenticated() {
    return !!localStorage.getItem('landgrid_access_token');
  }

  getStoredUser() {
    const userStr = localStorage.getItem('landgrid_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Make authenticated requests
   */
  async requestWithAuth(endpoint, options = {}) {
    if (!this.supportsAuth) {
      throw new Error('Authenticated API requests are not available in offline mode.');
    }

    const token = localStorage.getItem('landgrid_access_token');
    if (!token) {
      throw new Error('No access token available');
    }

    const authOptions = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };

    try {
      return await this.request(endpoint, authOptions);
    } catch (error) {
      // If token expired, try to refresh
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        try {
          await this.refreshToken();
          // Retry with new token
          const newToken = localStorage.getItem('landgrid_access_token');
          authOptions.headers.Authorization = `Bearer ${newToken}`;
          return await this.request(endpoint, authOptions);
        } catch (refreshError) {
          // Refresh failed, user needs to login again
          await this.logout();
          throw new Error('Authentication expired. Please login again.');
        }
      }
      throw error;
    }
  }

  /**
   * Progress tracking methods
   */
  async getProgress(moduleId, lessonId) {
    if (!this.supportsAuth) {
      const stored = localStorage.getItem('landgrid-progress');
      if (!stored) {
        return { success: true, data: null, offline: true };
      }

      try {
        const parsed = JSON.parse(stored);
        return { success: true, data: parsed, offline: true };
      } catch (error) {
        console.warn('Failed to parse offline progress:', error);
        return { success: false, error: 'INVALID_OFFLINE_PROGRESS' };
      }
    }

    try {
      const params = new URLSearchParams();
      if (moduleId) params.append('moduleId', moduleId);
      if (lessonId) params.append('lessonId', lessonId);

      const queryString = params.toString();
      const baseEndpoint = this.mode === 'netlify' ? '/progress-tracker' : '/progress';
      const endpoint = queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint;

      const response = await this.requestWithAuth(endpoint);
      return response;
    } catch (error) {
      console.error('Get progress failed:', error);
      throw error;
    }
  }

  async updateProgress(progressData) {
    if (!this.supportsAuth) {
      try {
        localStorage.setItem('landgrid-progress', JSON.stringify(progressData));
        return { success: true, data: progressData, offline: true };
      } catch (error) {
        console.error('Failed to persist offline progress:', error);
        throw error;
      }
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/progress-tracker' : '/progress';
      const response = await this.requestWithAuth(endpoint, {
        method: 'POST',
        body: progressData
      });
      return response;
    } catch (error) {
      console.error('Update progress failed:', error);
      throw error;
    }
  }

  /**
   * User profile methods
   */
  async getProfile() {
    if (!this.supportsAuth) {
      const user = this.getStoredUser();
      if (user) {
        return { success: true, data: user, offline: true };
      }
      throw new Error('Offline profile not found. Please create an offline session.');
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/users-profile' : '/users/profile';
      const response = await this.requestWithAuth(endpoint);
      return response;
    } catch (error) {
      console.error('Get profile failed:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    if (!this.supportsAuth) {
      try {
        const user = this.getStoredUser() || {};
        const updatedUser = { ...user, ...profileData, offline: true };
        localStorage.setItem('landgrid_user', JSON.stringify(updatedUser));
        return { success: true, data: updatedUser, offline: true };
      } catch (error) {
        console.error('Failed to update offline profile:', error);
        throw error;
      }
    }

    try {
      const endpoint = this.mode === 'netlify' ? '/users-profile' : '/users/profile';
      const response = await this.requestWithAuth(endpoint, {
        method: 'PUT',
        body: profileData
      });
      return response;
    } catch (error) {
      console.error('Update profile failed:', error);
      throw error;
    }
  }

  /**
   * Health check
   */
  async healthCheck() {
    try {
      const endpoint = this.mode === 'netlify' ? '/health' : '/status';
      const response = await this.request(endpoint);
      return { status: 'healthy', data: this.extractData(response) ?? response };
    } catch (error) {
      console.error('Health check failed:', error);
      return { status: 'unhealthy', error: error.message };
    }
  }

  /**
   * Calculate local quiz score for offline mode
   */
  calculateLocalScore(answers) {
    // Simple scoring - this would normally be done by the backend
    if (!answers || !Array.isArray(answers)) return 0;

    // For now, return a percentage based on answered questions
    const totalQuestions = answers.length;
    const answeredQuestions = answers.filter(answer => answer && answer.selectedAnswer).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  }

  /**
   * Get API status
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Check if running in offline mode
   */
  isOfflineMode() {
    return !this.initialized;
  }

  /**
   * Get base URL
   */
  getBaseURL() {
    return this.baseURL;
  }
}

export default APIService;






